import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { filter, map, switchMap, distinctUntilChanged, catchError } from 'rxjs/operators';
import { BehaviorSubject, of, forkJoin, Observable } from 'rxjs';
import { ExternalLinkService, ExternalLinkStatus } from './external-link.service';
import { ZoningCodeModel } from '../../models/calibrations/zoning-code-models/zoning-code.model';
import { environment } from '../../../environments/environment';
import IZoningCode = ZoneIQCalibration.IZoningCode;
import IResponse = ZoneIQCalibration.IResponse;
import { UsesService } from './uses.service';
import { SyncService } from './sync.service';
import { CalibrationGeneralService } from './calibration-general.service';
import { CalibrationParkingService } from './calibration-parking.service';
import { CalibrationOfficeCommercialService } from './calibration-office-commercial.service';
import { ToastrService } from 'ngx-toastr';
import notificationContentDictionary from '../../models/tables/dictionary/notificationsContent.dictionary';
import { CalibrationBonusService } from './calibration-bonus.service';
import { ZoneCombinationService } from './zone-combination.service';
import * as _ from 'lodash';
import { OverlayType } from '../../../shared/enums/overlay-type.enum';
import { OverlayTypologyService } from './overlay-typology.service';
import {
    IZCEntitiesUpdatedData,
    IZoningCodeModel,
    IZoningCodePublishResult
} from 'src/app/interfaces/zoning-code-model.interface';
import { IZoneCombinationModel } from 'src/app/interfaces/zone-combination-model.interface';
import { CalibrationUsesLabelModel } from '../../models';
// import ICalibrationUsesTypeLabel = ZoneIQCalibration.ICalibrationUsesTypeLabel;
import { IBaseFieldComponent } from 'src/app/interfaces/base-field-component.interface';
import { UnitSystem } from 'src/shared/enums/unit-system-enum';
import { BaseAPIService } from './base-api.service';
import useClassOptions from 'src/shared/enums/use-class-options';
import IUseClass = ZoneIQCalibration.IUseClass;
import IZoningCodesImportValidationResult = ZoneIQCalibration.IZoningCodesImportValidationResult;
import { zoningCodesImportValidationResultMapperFactory } from 'src/app/mappers/zoning-codes-import-validation-result.mapper';
import IZoningCodeDraftUpdateDTO = ZoneIQCalibration.IZoningCodeDraftUpdateDTO;
import IGridicsUseTag = ZoneIQCalibration.IGridicsUseTag;

const API = environment.ZONEIQ_CALIBRATION + '/v1/zoning-code';

@Injectable({
    providedIn: 'root'
})
export class ZoningCodeService extends BaseAPIService {
    private readonly zoningCodesSubject = new BehaviorSubject<IZoningCodeModel[]>(null);
    readonly zoningCodes = this.zoningCodesSubject.asObservable().pipe(distinctUntilChanged());

    syncZoningCodeChanges: any = null;

    selectedZoningCode: IZoningCodeModel = null;

    get zoningCodeId() {
        if (this.selectedZoningCode) {
            return this.selectedZoningCode.Id;
        }
        return null;
    }

    get originalZoningCodeId() {
        if (this.selectedZoningCode) {
            return this.selectedZoningCode.CloneId;
        }
        return null;
    }

    get zoningCodeName() {
        return this.selectedZoningCode ? this.selectedZoningCode.Name : '';
    }

    get hasZoningCode() {
        return !!this.selectedZoningCode;
    }

    get isZoningCodeValid() {
        return this.hasZoningCode && this.selectedZoningCode.isValidStatus;
    }

    get isZCValidWithoutRequiredFields() {
        return this.hasZoningCode && this.selectedZoningCode.isValid(true);
    }

    get isZCValidWithRequiredFields() {
        return this.hasZoningCode && this.selectedZoningCode.isValid();
    }

    get isZoningCodeChanged() {
        return this.hasZoningCode && this.selectedZoningCode.isChanged;
    }

    get unitSystem(): UnitSystem {
        return _.get(this.selectedZoningCode, 'UnitSystem', UnitSystem.IMPERIAL);
    }

    get useClasses(): IUseClass[] {
        return !_.isEmpty(_.get(this.selectedZoningCode, 'UseClasses'))
            ? this.selectedZoningCode.UseClasses
            : useClassOptions;
    }

    get gridicsUseTags(): IGridicsUseTag[] {
        return !_.isEmpty(_.get(this.selectedZoningCode, 'GridicsUseTags'))
            ? this.selectedZoningCode.GridicsUseTags
            : [];
    }

    constructor(
        private readonly externalLinkService: ExternalLinkService,
        private readonly http: HttpClient,
        private readonly usesService: UsesService,
        private readonly syncService: SyncService,
        private readonly zoneCombinationService: ZoneCombinationService,
        private readonly overlayTypologyService: OverlayTypologyService,
        private readonly calibrationGeneralService: CalibrationGeneralService,
        private readonly calibrationBonusService: CalibrationBonusService,
        private readonly calibrationParkingService: CalibrationParkingService,
        private readonly calibrationOfficeCommercialService: CalibrationOfficeCommercialService,
        private readonly toastrService: ToastrService
    ) {
        super();
        this.externalLinkService.status
            .pipe(
                filter(status => status === ExternalLinkStatus.PROVIDED),
                switchMap(() => this.fetchZoningCodes()),
                catchError(err => {
                    console.log('ZoningCodeService.constructor => catchError');
                    console.log(err);
                    return of(null);
                })
            )
            .subscribe(zoningCode => {
                this.selectedZoningCode = ZoningCodeModel.factory(zoningCode as IZoningCode);
                // if (this.externalLinkService.isZCImportUseLabelsPage()) {
                //     this.loadDataForImport();
                // }
                this.syncService.registerZCEditing();
                return this.zoningCodesSubject.next([this.selectedZoningCode]);
            });

        this.syncService.changes.subscribe((changes: IZCEntitiesUpdatedData) => {
            this.updateZoningCodeEntities(changes as IZCEntitiesUpdatedData);
        });

        this.syncService.deleteChanges.subscribe((changes: IZCEntitiesUpdatedData) => {
            this.updateZoningCodeEntities(changes as IZCEntitiesUpdatedData, true);
        });

        this.syncService.syncChanges.subscribe((changes: ZoningCodeModel) => {
            this.syncDraft(changes as ZoningCodeModel);
        });

        this.syncService.valueChanged.subscribe((_value: IBaseFieldComponent) => {
            if (this.hasZoningCode) {
                this.selectedZoningCode.clearValidationResults();
            }
        });
    }

    private fetchZoningCodes() {
        const url = `${API}/${this.externalLinkService.ExternalLink}`;
        let params = new HttpParams();
        if (this.externalLinkService.isDraftPage()) {
            params = params.append('full', 'true').append('isDraft', 'true');
        }
        /**
         * TODO :: The parameters should be defined by the components.
         * Need to move Zoning Code request logic to components.
         */
        if (this.externalLinkService.isZCImportUseValuesPage()) {
            params = params
                .append('full', 'true')
                .append(
                    'listOfRelations',
                    '["AllowedUses","ZoneCombinations","CalibrationUsesLabels","CalibrationUsesTypes","CalibrationUsesTypeLabels"]'
                )
                .append('listOfZCmbRelations', '[]');
        }
        return this.http
            .get<IResponse>(url, { params })
            .pipe(map(response => response.data as IZoningCode));
    }

    private syncDraft(changesZoningCode) {
        this.syncZoningCodeChanges = changesZoningCode && changesZoningCode.zoningCodeDifference;

        this.toastrService.warning(notificationContentDictionary.SynchronizationMessage.label, 'Warning', {
            closeButton: true,
            disableTimeOut: true
        });

        this.syncService.triggerChangeDetection();
    }

    saveChangedData() {
        if (!this.selectedZoningCode) {
            return forkJoin([]);
        }

        const tables = this.selectedZoningCode.tables;
        const notAllowedUse = this.selectedZoningCode.notAllowedUse;

        const overlayTypology = [];
        const zoneCombinations = [];

        this.selectedZoningCode.ZoneCombinations.forEach((zcmb: IZoneCombinationModel) => {
            if (zcmb.isChanged) {
                zoneCombinations.push(zcmb.getChangedValues());
            }
        });

        this.selectedZoningCode.Overlays.forEach(overlay => {
            if (overlay.BuildingCalibrationType !== OverlayType.BUILDING_TYPOLOGY) {
                overlay.OverlayTypology.forEach(overTypo => {
                    if (overTypo.isChanged) {
                        overlayTypology.push(overTypo.getChangedValues());
                    }
                });
            }
        });

        const changedUseLabels = this.selectedZoningCode.CalibrationUsesLabels.filter(
            (label: CalibrationUsesLabelModel) => label.isChanged
        ).map((label: CalibrationUsesLabelModel) => label.getChangedValues());

        const usesToSave = tables.zones.usesTable
            .getChangedData(notAllowedUse)
            .concat(
                tables.overlays.usesTable.getChangedData(notAllowedUse),
                tables.typologies.usesTable.getChangedData(notAllowedUse)
            );
        const usesToDelete = tables.zones.usesTable
            .getDataToDelete(notAllowedUse)
            .concat(
                tables.overlays.usesTable.getDataToDelete(notAllowedUse),
                tables.typologies.usesTable.getDataToDelete(notAllowedUse)
            );

        const generalDataToSave = tables.zones.generalTable
            .getChangedData()
            .concat(tables.overlays.generalTable.getChangedData(), tables.typologies.generalTable.getChangedData());
        const parkingDataToSave = tables.zones.parkingTable
            .getChangedData()
            .concat(tables.overlays.parkingTable.getChangedData(), tables.typologies.parkingTable.getChangedData());
        const officeCommercialDataToSave = tables.zones.useCapacityTable
            .getChangedData()
            .concat(
                tables.overlays.useCapacityTable.getChangedData(),
                tables.typologies.useCapacityTable.getChangedData()
            );
        const bonusDataToSave = tables.zones.bonusTable
            .getChangedData()
            .concat(tables.overlays.bonusTable.getChangedData(), tables.typologies.bonusTable.getChangedData());

        const saveQueries = [];

        if (usesToSave.length) {
            saveQueries.push(this.usesService.saveChanges(usesToSave));
        }

        if (changedUseLabels.length) {
            saveQueries.push(this.usesService.addOrUpdateUsesLabels(changedUseLabels));
        }

        if (generalDataToSave.length) {
            saveQueries.push(this.calibrationGeneralService.saveChanges(generalDataToSave));
        }

        if (zoneCombinations.length) {
            saveQueries.push(this.zoneCombinationService.saveChanges(zoneCombinations));
        }

        if (overlayTypology.length) {
            saveQueries.push(this.overlayTypologyService.saveChanges(overlayTypology));
        }

        if (parkingDataToSave.length) {
            saveQueries.push(this.calibrationParkingService.saveChanges(parkingDataToSave));
        }

        if (officeCommercialDataToSave.length) {
            saveQueries.push(this.calibrationOfficeCommercialService.saveChanges(officeCommercialDataToSave));
        }

        if (bonusDataToSave.length) {
            saveQueries.push(this.calibrationBonusService.saveChanges(bonusDataToSave));
        }

        if (usesToDelete.length) {
            saveQueries.push(this.usesService.deleteUses(usesToDelete));
        }

        return forkJoin(saveQueries);
    }

    publishDraft() {
        const url = `${API}/drafts/${this.externalLinkService.ExternalLink}/publish`;
        return this.http.post<IResponse>(url, {}).pipe(map(response => response.data as IZoningCodePublishResult));
    }

    updateDraft(draftData: IZoningCodeDraftUpdateDTO) {
        const url = `${API}/drafts/${this.externalLinkService.ExternalLink}`;
        return this.http.patch<IResponse>(url, draftData).pipe(
            map(response => response.data),
            map(data => this.updateSelectedZoningCodePublishRequired(data.IsPublishingRequired))
        );
    }

    importZCmbUsesValues(zoningCodeId: number, usesValues: FormData) {
        if (!zoningCodeId || !usesValues) {
            return of(null);
        }
        const url = `${API}/${zoningCodeId}/zone-combinations/import-uses-values`;
        return this.http
            .post<IResponse>(url, usesValues)
            .pipe(map(response => response.data))
            .pipe(
                map(data => {
                    this.selectedZoningCode = ZoningCodeModel.factory(data as IZoningCode);
                    return this.selectedZoningCode;
                })
            );
    }

    validateZCmbUsesValues(zoningCodeId: number, usesValues: FormData) {
        if (!zoningCodeId || !usesValues) {
            return of(null);
        }
        const url = `${API}/${zoningCodeId}/zone-combinations/import-uses-values/validate`;
        return this.http
            .post<IResponse>(url, usesValues)
            .pipe(map(response => response.data))
            .pipe(
                map(data => {
                    this.selectedZoningCode = ZoningCodeModel.factory(data as IZoningCode);
                    return this.selectedZoningCode;
                })
            );
    }

    importZCmbGeneralValues(zoningCodeId: number, generalValues: FormData) {
        if (!zoningCodeId || !generalValues) {
            return of(null);
        }
        const url = `${API}/${zoningCodeId}/zone-combinations/import-general-values`;
        return this.http
            .post<IResponse>(url, generalValues)
            .pipe(map(response => response.data))
            .pipe(
                map(data => {
                    this.selectedZoningCode = ZoningCodeModel.factory(data as IZoningCode);
                    return this.selectedZoningCode;
                })
            );
    }

    importZCmbOfficeCommercialValues(zoningCodeId: number, officeCommercialValues: FormData) {
        if (!zoningCodeId || !officeCommercialValues) {
            return of(null);
        }
        const url = `${API}/${zoningCodeId}/zone-combinations/import-use-capacity-values`;
        return this.http
            .post<IResponse>(url, officeCommercialValues)
            .pipe(map(response => response.data))
            .pipe(
                map(data => {
                    this.selectedZoningCode = ZoningCodeModel.factory(data as IZoningCode);
                    return this.selectedZoningCode;
                })
            );
    }

    validateZCmbGeneralValues(zoningCodeId: number, generalValues: FormData) {
        if (!zoningCodeId || !generalValues) {
            return of(null);
        }
        const url = `${API}/${zoningCodeId}/zone-combinations/import-general-values/validate`;
        return this.http
            .post<IResponse>(url, generalValues)
            .pipe(map(response => response.data))
            .pipe(
                map(data => {
                    this.selectedZoningCode = ZoningCodeModel.factory(data as IZoningCode);
                    return this.selectedZoningCode;
                })
            );
    }

    validateZCmbOfficeCommercialValues(zoningCodeId: number, officeCommercialValues: FormData) {
        if (!zoningCodeId || !officeCommercialValues) {
            return of(null);
        }
        const url = `${API}/${zoningCodeId}/zone-combinations/import-use-capacity-values/validate`;
        return this.http
            .post<IResponse>(url, officeCommercialValues)
            .pipe(map(response => response.data))
            .pipe(
                map(data => {
                    this.selectedZoningCode = ZoningCodeModel.factory(data as IZoningCode);
                    return this.selectedZoningCode;
                })
            );
    }

    updateZoningCodeEntities(entitiesUpdatedData: IZCEntitiesUpdatedData, toDelete = false, showNotification = true) {
        if (!entitiesUpdatedData) {
            return;
        }
        const zoningCode = this.zoningCodesSubject.value.find(zc => zc.Id === entitiesUpdatedData.zoningCodeId);
        if (!zoningCode) {
            this.toastrService.error(
                `${notificationContentDictionary.zoningCodeAbsent.label} ${entitiesUpdatedData.zoningCodeId} was not found`,
                'Error'
            );
            return;
        }

        zoningCode.updateData(
            entitiesUpdatedData.entityName,
            entitiesUpdatedData.models,
            entitiesUpdatedData.zoningCodeStatus,
            toDelete
        );

        if (showNotification) {
            this.toastrService.success(
                `${_.get(notificationContentDictionary[entitiesUpdatedData.entityName], 'label')} ${
                    entitiesUpdatedData.zoningCodeId
                }`,
                'Success'
            );
        }

        this.syncService.triggerChangeDetection();
    }

    cancelZCChanges() {
        this.selectedZoningCode.cancelChanges();
        this.syncService.triggerChangeDetection();
    }

    importZoningCodes(
        zoningCodesData: FormData,
        validateOnly?: boolean
    ): Observable<IZoningCodesImportValidationResult> {
        if (!zoningCodesData) {
            return of(null);
        }
        const url = `${API}/import${validateOnly ? '/validate' : ''}`;
        return this.http
            .post<IResponse>(url, zoningCodesData)
            .pipe(map(response => response.data))
            .pipe(
                map(data => {
                    const result = zoningCodesImportValidationResultMapperFactory().fromDTO(data);
                    return result;
                })
            );
    }

    getZoningCodeImportTemplate(): Observable<HttpResponse<Blob>> {
        const url = `${API}/import/template`;
        return this.http.get(url, { responseType: 'blob', observe: 'response' });
    }

    validateZoneDescriptions(
        zoningCodeId: number,
        fileData: FormData
    ): Observable<IZoningCodeModel> {
        if (!zoningCodeId || !fileData) {
            return of(null);
        }
        const url = `${API}/${zoningCodeId}/import/zone-descriptions/validate`;
        return this.http
            .post<IResponse>(url, fileData)
            .pipe(map(response => response.data))
            .pipe(
                map(data => {
                    this.selectedZoningCode = ZoningCodeModel.factory(data as IZoningCode);
                    return this.selectedZoningCode;
                })
            );
    }

    importZoneDescriptions(zoningCodeId: number, fileData: FormData): Observable<IZoningCodeModel> {
        if (!zoningCodeId || !fileData) {
            return of(null);
        }
        const url = `${API}/${zoningCodeId}/import/zone-descriptions`;
        return this.http
            .post<IResponse>(url, fileData)
            .pipe(map(response => response.data))
            .pipe(
                map(data => {
                    this.selectedZoningCode = ZoningCodeModel.factory(data as IZoningCode);
                    return this.selectedZoningCode;
                })
            );
    }

    getZoneDescriptionsImportTemplate(zoningCodeId: number): Observable<HttpResponse<Blob>> {
        const url = `${API}/${zoningCodeId}/import/zone-descriptions/template`;
        return this.http.get(url, { responseType: 'blob', observe: 'response' });
    }

    validateCalibrationSetTableFieldAttributes(
        zoningCodeId: number,
        fileData: FormData
    ): Observable<IZoningCodeModel> {
        if (!zoningCodeId || !fileData) {
            return of(null);
        }
        const url = `${API}/${zoningCodeId}/import/calibration-set-table-field-attributes/validate`;
        return this.http
            .post<IResponse>(url, fileData)
            .pipe(map(response => response.data))
            .pipe(
                map(data => {
                    this.selectedZoningCode = ZoningCodeModel.factory(data as IZoningCode);
                    return this.selectedZoningCode;
                })
            );
    }

    importCalibrationSetTableFieldAttributes(zoningCodeId: number, fileData: FormData): Observable<IZoningCodeModel> {
        if (!zoningCodeId || !fileData) {
            return of(null);
        }
        const url = `${API}/${zoningCodeId}/import/calibration-set-table-field-attributes`;
        return this.http
            .post<IResponse>(url, fileData)
            .pipe(map(response => response.data))
            .pipe(
                map(data => {
                    this.selectedZoningCode = ZoningCodeModel.factory(data as IZoningCode);
                    return this.selectedZoningCode;
                })
            );
    }

    getCalibrationSetTableFieldAttributesImportTemplate(zoningCodeId: number): Observable<HttpResponse<Blob>> {
        const url = `${API}/${zoningCodeId}/import/calibration-set-table-field-attributes/template`;
        return this.http.get(url, { responseType: 'blob', observe: 'response' });
    }

    getZoneCombinationUsesValuesImportTemplate(zoningCodeId?: number): Observable<HttpResponse<Blob>> {
        const url = `${API}/zone-combinations/import-uses-values/template${
            zoningCodeId ? `?zoningCodeId=${zoningCodeId}` : ''
        }`;
        return this.http.get(url, { responseType: 'blob', observe: 'response' });
    }

    getZoneCombinationCalibrationGeneralValuesImportTemplate(zoningCodeId?: number): Observable<HttpResponse<Blob>> {
        const url = `${API}/zone-combinations/import-general-values/template${
            zoningCodeId ? `?zoningCodeId=${zoningCodeId}` : ''
        }`;
        return this.http.get(url, { responseType: 'blob', observe: 'response' });
    }

    getZoneCombinationCalibrationOfficeCommercialValuesImportTemplate(
        zoningCodeId?: number
    ): Observable<HttpResponse<Blob>> {
        const url = `${API}/zone-combinations/import-use-capacity-values/template${
            zoningCodeId ? `?zoningCodeId=${zoningCodeId}` : ''
        }`;
        return this.http.get(url, { responseType: 'blob', observe: 'response' });
    }

    getZCExternalLink(zoningCodeId: string): Observable<string> {
        if (!zoningCodeId) {
            return of(null);
        }
        const url = `${API}/${zoningCodeId}/external-link`;
        return this.http.get<IResponse>(url).pipe(map(response => response.data));
    }

    getZCImportUrl(zoningCodeId, entityTab: string, calibrationTab: string): Observable<string> {
        if (!zoningCodeId || !entityTab || !calibrationTab) {
            return of(null);
        }

        return this.getZCExternalLink(zoningCodeId).pipe(
            map(externalLink => {
                return externalLink ? `/import/zoning-code/${externalLink}/${entityTab}/${calibrationTab}` : null;
            })
        );
    }

    updateSelectedZoningCodePublishRequired(isPublishingRequired: boolean) {
        if (!this.selectedZoningCode) {
            return of(null);
        }

        this.selectedZoningCode.IsPublishingRequired = isPublishingRequired;
    }

    static getZoningCodeFileNamePrefix(zoningCodeName: string): string {
        return zoningCodeName
            ? zoningCodeName
                  .replace(',', '')
                  .split(' ')
                  .join('-')
            : '';
    }
}
